/**
 * Full-layout chrome shown while the onboarding tour runs and no pack is selected,
 * so [data-tour] targets exist for spotlight steps (packs, playlist, preview, notes).
 */

/** Fake thumbnail strip — same structure as real slides for consistent tour highlights */
export function TourPlaceholderSlidesStrip() {
  const items = [
    { n: 1, active: false, tone: 'a' },
    { n: 2, active: true, tone: 'b' },
    { n: 3, active: false, tone: 'c' },
    { n: 4, active: false, tone: 'd' },
  ];
  return (
    <>
      {items.map((item) => (
        <div
          key={item.n}
          className={`slide-preview-item tour-placeholder-thumb ${item.active ? 'active' : ''}`}
          data-tour={item.active ? 'tour-slide-shortcut-spotlight' : undefined}
          aria-hidden="true"
        >
          <div className={`tour-placeholder-thumb-fill tour-placeholder-thumb-fill--${item.tone}`} />
          <span className="slide-number">{item.n}</span>
          {item.active ? (
            <>
              <span className="slide-shortcut-badge" aria-hidden="true">
                K
              </span>
              <button
                type="button"
                className="slide-shortcut-assign"
                title="Assign keyboard shortcut (tour preview)"
                tabIndex={-1}
                disabled
              >
                <i className="fa-regular fa-keyboard" aria-hidden="true" />
              </button>
            </>
          ) : null}
        </div>
      ))}
    </>
  );
}

export function TourPlaceholderSidebar({ onOpenPacksModal, autoAdvance, setAutoAdvance }) {
  return (
    <>
      <div className="library-section tour-placeholder-library-packs" data-tour="tour-packs">
        <div className="section-header">
          <h2>Packs</h2>
          <button
            type="button"
            className="add-pack-button"
            onClick={onOpenPacksModal}
            title="Create or select pack"
            aria-label="Create or select pack"
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
          </button>
        </div>
        <div className="library-items tour-placeholder-items" aria-hidden="true">
          <div className="tour-placeholder-row tour-placeholder-row--pack">
            <span className="tour-placeholder-swatch" />
            <span className="tour-placeholder-caption">Example pack</span>
          </div>
          <div className="tour-placeholder-row tour-placeholder-row--pack">
            <span className="tour-placeholder-swatch tour-placeholder-swatch--dim" />
            <span className="tour-placeholder-caption">Another folder</span>
          </div>
        </div>
      </div>

      <div
        className="library-section playlist-section tour-placeholder-library-playlist"
        id="playlist-section"
        data-tour="tour-playlist"
      >
        <div className="section-header">
          <h2>Playlist</h2>
          <button
            type="button"
            className="add-pack-button"
            disabled
            title="Add decks after the tour"
            aria-label="Add to playlist (disabled during tour)"
          >
            <i className="fa-solid fa-plus" aria-hidden="true" />
          </button>
        </div>
        <div className="library-items tour-placeholder-items" aria-hidden="true">
          <div className="tour-placeholder-row tour-placeholder-row--deck">
            <span className="tour-placeholder-deck-num">1</span>
            <span className="tour-placeholder-caption">Opening slides</span>
          </div>
          <div className="tour-placeholder-row tour-placeholder-row--deck">
            <span className="tour-placeholder-deck-num">2</span>
            <span className="tour-placeholder-caption">Main deck</span>
          </div>
        </div>
        <div className="playlist-footer">
          <label className="auto-advance-toggle">
            <span className="auto-advance-label">Auto-advance</span>
            <button
              type="button"
              className={`auto-advance-switch ${autoAdvance ? 'on' : 'off'}`}
              onClick={() => setAutoAdvance(!autoAdvance)}
              aria-pressed={autoAdvance}
            >
              <span className="auto-advance-knob" />
            </button>
          </label>
          <button
            type="button"
            className="remote-indicator-btn remote-indicator-btn--active"
            data-tour="tour-remote"
            title="Phone / tablet remote"
            aria-label="Phone or tablet remote (tour)"
            disabled
          >
            <i className="fa-solid fa-mobile-screen-button" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  );
}

export function TourPlaceholderMain({ previewHeightRatio }) {
  const previewPct = previewHeightRatio * 100;
  const notesPct = (1 - previewHeightRatio) * 100;
  return (
    <div className="preview-notes-container tour-placeholder-main">
      <div
        className="preview-area tour-placeholder-preview-area"
        data-tour="tour-preview"
        style={{
          flex: `1 1 ${previewPct}%`,
          minHeight: 'min(44vh, 420px)',
        }}
      >
        <div className="tour-placeholder-deck-mock" role="presentation">
          <div className="tour-placeholder-deck-chrome">
            <span className="tour-placeholder-deck-dot" />
            <span className="tour-placeholder-deck-dot" />
            <span className="tour-placeholder-deck-title">Main presentation — Slide 2</span>
          </div>
          <div className="tour-placeholder-slide-body">
            <div className="tour-placeholder-slide-title">Welcome</div>
            <ul className="tour-placeholder-slide-bullets">
              <li>One point you&apos;d show on stage</li>
              <li>Another key idea</li>
              <li>A call to action</li>
            </ul>
            <div className="tour-placeholder-slide-footer">Placeholder — real preview uses your Google Slides</div>
          </div>
        </div>
      </div>

      <div className="preview-resizer tour-placeholder-resizer" title="Resize preview and notes" aria-hidden="true" />

      <div
        className="notes-section tour-placeholder-notes-section"
        data-tour="tour-notes"
        style={{
          flex: `1 1 ${notesPct}%`,
          minHeight: 'min(26vh, 280px)',
        }}
      >
        <div className="notes-text tour-placeholder-notes-text">
          <p className="tour-placeholder-notes-title">Speaker notes</p>
          <p className="tour-placeholder-notes-lead">
            What you say in the room — not on the slide — shows here. In Deck Pack this comes from Google Slides
            speaker notes for the selected slide.
          </p>
          <ul className="tour-placeholder-notes-sample">
            <li>Pause after the second bullet for questions.</li>
            <li>Transition: &quot;Let&apos;s move to the demo…&quot;</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
